<template>
  <div>
    <div class="panel-heading order-heading-with-stats">
      <h4 class="panel-title">{{ $t('invoice.shippingInvoiceTitle') }}</h4>

      <div class="order-heading-right">
        <div class="order-toolbar-buttons">
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="toggleFiltersVisible">
            <i class="material-icons">{{ showFilters ? 'expand_less' : 'expand_more' }}</i>
            <span v-if="showButtonLabels">{{ $t('orders.filtersToggleLabel') }}</span>
          </a>
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="reloadTable">
            <i class="material-icons">cached</i>
            <span v-if="showButtonLabels">{{ $t('datatable.reloadShortLabel') }}</span>
          </a>
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="sendToExport">
            <i class="material-icons">explicit</i>
            <span v-if="showButtonLabels">{{ $t('datatable.exportButton') }}</span>
          </a>
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="prepareToPrint">
            <i class="material-icons" style="color:#006b8d">print</i>
            <span v-if="showButtonLabels">{{ $t('datatable.prepareToPrintShortLabel') }}</span>
          </a>
          <a v-if="$_has_perm('fast_payment_invoice')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="confirmFastPaymentAll">
            <i class="material-icons" style="color:#2e7d32">payments</i>
            <span v-if="showButtonLabels">{{ $t('datatable.fastPaymentShortLabel') }}</span>
          </a>
          <a v-if="$_has_perm('mark_invoice_received')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="confirmReceiveAll">
            <i class="material-icons" style="color:#1565c0">receipt</i>
            <span v-if="showButtonLabels">{{ $t('datatable.receiveButton') }}</span>
          </a>
          <a v-if="$_has_perm('unmark_invoice_received')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="confirmUnreceiveAll">
            <i class="material-icons" style="color:#c62828">undo</i>
            <span v-if="showButtonLabels">{{ $t('datatable.unreceiveButton') }}</span>
          </a>
          <a v-if="$_has_perm('print_received_invoices')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="receivePrintList">
            <i class="material-icons" style="color:#006b8d">print</i>
            <span v-if="showButtonLabels">{{ $t('datatable.printReceiveShortLabel') }}</span>
          </a>
          <a v-if="$_has_perm('mark_invoice_out_office')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="confirmOutOfficeAll">
            <i class="material-icons" style="color:#c62828">exit_to_app</i>
            <span v-if="showButtonLabels">{{ $t('datatable.outOfficeButton') }}</span>
          </a>
          <a v-if="$_has_perm('merge_print_shipping_invoices')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="mergePrintList">
            <i class="material-icons" style="color:#6a1b9a">merge_type</i>
            <span v-if="showButtonLabels">{{ $t('datatable.mergeAndPrintShortLabel') }}</span>
          </a>
          <a class="order-toolbar-display-toggle" @click="$_toggle_toolbar_button_display()">
            <i class="material-icons">{{ showButtonLabels ? 'label_off' : 'label' }}</i>
          </a>
        </div>

        <!-- Totals reflect only the currently loaded page of results (same "current page"
             caveat used on allOrder.vue's stat cards) -- footer is null until the first
             getResults() response returns, hence the fallback '0'. -->
        <div class="order-stats-row order-stats-row--compact">
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--blue"><i class="material-icons">receipt</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('invoice.shippingInvoiceTitle') }}</span>
              <span class="order-stat-value">{{ totalResult }}</span>
              <span class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--green"><i class="material-icons">attach_money</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('printLogs.totalAmountLabel') }}</span>
              <span class="order-stat-value">{{ footer ? footer.total_amount : '0' }}</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--purple"><i class="material-icons">check_circle</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('logs.totalPaidLabel').replace(':', '') }}</span>
              <span class="order-stat-value">{{ footer ? footer.total_paid : '0' }}</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--orange"><i class="material-icons">payment</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('logs.totalRemainingLabel').replace(':', '') }}</span>
              <span class="order-stat-value">{{ footer ? footer.total_remaining : '0' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="panel-body"
      style="display: block;"
    >
      <div class="row">
        <preDatatableComponent
          ref="preDatatable"
          :hideReloadButton="true"
          :collapsibleFilters="true"
          :filtersVisible="showFilters"
          :sendToExport="sendToExport"
          :confirmFastPaymentAll="confirmFastPaymentAll"
          :prepareToPrint="prepareToPrint"
          :confirmReceiveAll="confirmReceiveAll"
          :confirmUnreceiveAll="confirmUnreceiveAll"
          :receivePrintList="receivePrintList"
          :confirmOutOfficeAll="confirmOutOfficeAll"
          :mergePrintList="mergePrintList"
          :removeAddButton="true"
          :removeWarehouseDeliverButton="true"
          :tableData="tableData"
          :defaultFromDate="defaultFromDate"
          :defaultToDate="defaultToDate"
          :resetGetResults="resetGetResults"
          :resetGetResults2="resetGetResults2"
          :searchMe="searchMe"
          :enableCustomerSearchSuggestions="true"
          :pagination="pagination"
          :perPageFix="perPageFix"
          :filterTable="filterTable"
          :statusFilterItems="paymentStatusFilterItems"
          :statusFilterChange="handlePaymentStatusFilter"
          :showReceivedFilter="true"
          :receivedFilterChange="handleReceivedFilter"
          :columns="columns"
          :showHideColumn="showHideColumn"
          :storageKey="'cols_orderInvoices_v6'"
          :defaultHideCol="defaultHideCol"
          :resultsVersion="resultsVersion"
          :filterMade="filterMade"
          :totalResult="totalResult"
          :permissions="{ fast_payment_permission: 'fast_payment_invoice', receive_permission: 'mark_invoice_received', unreceive_permission: 'unmark_invoice_received', merge_print_permission: 'merge_print_shipping_invoices', out_office_permission: 'mark_invoice_out_office' }"
          @branch-edit="branchEdit"
        >
          <template v-slot:custom-filter-before-date>
            <template v-if="showFilters">
            <v-flex
              class="filter-field px-2"
              style="align-self: flex-end;"
            >
              <v-select
                :items="dateFieldFilterItems"
                v-model="tableData.date_field"
                @change="getResults()"
                :label="$t('columns.dateFilterByLabel')"
                single-line
                hide-details
                item-text="text"
                item-value="value"
              ></v-select>
            </v-flex>
            </template>
          </template>

          <template v-slot:custom-filter>
            <template v-if="showFilters">
            <v-flex
              class="filter-field px-2"
              style="align-self: flex-end;"
            >
              <v-select
                :items="generatedByFilterItems"
                :value="generatedByFilterVal"
                :label="$t('columns.generatedBy')"
                multiple
                clearable
                hide-details
                item-text="text"
                item-value="value"
                @input="handleGeneratedByFilter"
              >
                <template slot="selection" slot-scope="{ index }">
                  <span v-if="index === 0">{{ (generatedByFilterVal || []).length }} {{ $t('columns.selectedSuffix') }}</span>
                </template>
              </v-select>
            </v-flex>
            <v-flex
              class="filter-field px-2"
              style="align-self: flex-end;"
            >
              <v-select
                :items="officeStatusFilterItems"
                :value="officeStatusFilterVal"
                :label="$t('columns.outOffice')"
                multiple
                clearable
                hide-details
                item-text="text"
                item-value="value"
                @input="handleOfficeStatusFilter"
              >
                <template slot="selection" slot-scope="{ index }">
                  <span v-if="index === 0">{{ (officeStatusFilterVal || []).length }} {{ $t('columns.selectedSuffix') }}</span>
                </template>
              </v-select>
            </v-flex>
            <v-flex
              class="filter-field px-2"
              style="align-self: flex-end;"
            >
              <v-select
                :items="refundFilterItems"
                :value="refundFilterVal"
                :label="$t('orders.refundTooltip')"
                multiple
                clearable
                hide-details
                item-text="text"
                item-value="value"
                @input="handleRefundFilter"
              >
                <template slot="selection" slot-scope="{ index }">
                  <span v-if="index === 0">{{ (refundFilterVal || []).length }} {{ $t('columns.selectedSuffix') }}</span>
                </template>
              </v-select>
            </v-flex>
            </template>
          </template>
        </preDatatableComponent>
        <div class="col-xs-12">
          <div class="row text-center">
            <span
              id="test-editable"
              style="opacity:0"
            ></span>
          </div>
          <div class="table-responsive oc-table-shell">
          <datatable
            :sortDir="tableData.sortDir"
            :columns="columns"
            :sortByColumn="sortByColumn"
            :searchOneCol="searchOneCol"
            :sortKey="sortKey"
            :sortOrders="sortOrders"
            :checkAll="checkAll"
            :unCheckAll="unCheckAll"
          >
            <tbody>
              <tr
                v-for="r in apiResults"
                :key="r.order_invoice_id"
              >
                <td>
                  <input
                    type="checkbox"
                    :value="r.order_invoice_id"
                    v-model="selectedIds"
                  />
                </td>
                <td class="oc-cell">
                  <div class="oc-order-number">{{ r.invoice_number }}</div>
                  <div class="oc-order-sub">{{ r.country }}</div>
                </td>
                <!-- <td>{{ r.branch_name }}</td> -->
                <td class="oc-cell" style="max-width: 140px; white-space: normal; word-break: break-word;">{{ r.order_ids || '—' }}</td>
                <td class="oc-cell ws-nowrap">
                  <span v-if="r.customer_id_fk" class="oc-customer-id">ID: {{ r.customer_id_fk }}</span>
                  <span v-if="r.full_name && r.full_name.trim().split(/\s+/).length > 3" style="word-break: break-word; white-space: pre-line;">
                    {{ r.full_name.replace(/((?:\S+\s+){3})/g, '$1\n') }}
                  </span>
                  <span v-else>
                    {{ r.full_name }}
                  </span>
                  <open-whatsapp-and-viber :phoneNumber="r.phone_number" />
                  <div class="oc-item-sub" v-if="r.phone_number">{{ $t('columns.phone') }}: {{ r.phone_number }}</div>
                </td>
                <td class="oc-cell customer-note-color"> {{ r.customer_note }} </td>
                <td class="oc-cell"><span v-if="r.handling_fee > 0" class="ship-only-label">{{ $t('columns.shipOnly') }}</span></td>
                <td class="oc-cell">{{ r.weight }}</td>
                <td class="oc-cell">{{ r.volume }}</td>
                <td class="oc-cell">{{ r.handling_fee }}</td>
                <td class="combined-cell-td oc-cell">
                  <div class="combined-cell">
                    <div><span class="combined-cell-label">{{ $t('columns.shipment') }}:</span> {{ r.shipment || '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.tax') }}:</span> {{ r.tax || '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.deliveryPrice') }}:</span> {{ r.delivery_price || '—' }}</div>
                  </div>
                </td>
                <td class="combined-cell-td oc-cell">
                  <div class="combined-cell">
                    <div><span class="combined-cell-label">{{ $t('columns.totalPrice') }}:</span> {{ r.total_price || '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.discount') }}:</span> {{ r.discount || '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.amount') }}:</span> {{ r.net_price || '—' }}</div>
                  </div>
                </td>
                <td class="combined-cell-td oc-cell">
                  <div class="combined-cell">
                    <div><span class="combined-cell-label">{{ $t('columns.paidAmount') }}:</span> {{ r.paid_amount || '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.remaining') }}:</span> {{ r.remaining_amount || '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('orders.refundTooltip') }}:</span> {{ r.refund || '—' }}</div>
                  </div>
                </td>
                <td class="oc-cell">
                  <span
                    v-if="r.payment_status"
                    class="oc-status-chip"
                    :style="{ background: statusColor(r.payment_status, r.edit_count).bg, color: statusColor(r.payment_status, r.edit_count).color }"
                  >{{ statusLabel(r.payment_status) }}</span>
                </td>
                <td class="oc-cell">
                  <span
                    v-if="r.office_status"
                    class="oc-status-chip"
                    :style="{ background: officeStatusColor(r).bg, color: officeStatusColor(r).color }"
                  >{{ officeStatusLabel(r) }}</span>
                </td>
                <td class="oc-cell">
                  <span
                    v-if="r.received_at"
                    class="oc-status-chip"
                    :style="{ background: receivedStatusColor(r).bg, color: receivedStatusColor(r).color }"
                  >{{ receivedStatusLabel(r) }}</span>
                </td>
                <td class="oc-cell">{{ formatReceivedDate(r.received_at) }}</td>
                <td class="oc-cell">{{ r.received_amount != null ? r.received_amount : '—' }}</td>
                <td class="oc-cell">{{ r.note }}</td>
                <td class="oc-cell ws-nowrap">
                  <span class="ovd-date-stack">
                    <span class="ovd-date-line">{{ r.created_at | moment('YYYY-MM-DD') }}</span>
                    <span class="ovd-time-line">{{ r.created_at | moment('hh:mm A') }}</span>
                  </span>
                </td>
                <td class="oc-cell">{{ r.delivery_address || '—' }}</td>
                <td class="oc-cell">
                  <span
                    v-if="generatedByLabel(r.generated_by)"
                    class="oc-status-chip"
                    :style="{ background: generatedByColor(r.generated_by).bg, color: generatedByColor(r.generated_by).color }"
                  >{{ generatedByLabel(r.generated_by) }}</span>
                  <span v-else>—</span>
                </td>
                <td class="oc-cell oc-actions-cell">
                  <div class="oc-actions-grid">
                    <a
                      :tooltip="$t('invoice.invoiceTooltip')"
                      class="oc-action-btn"
                      @click="viewRecord(r.order_invoice_id)"
                    >
                      <i
                        class="fa fa-file-text-o"
                        style="color: #666"
                      ></i>
                    </a>

                    <a
                      v-if="$_has_perm('record_invoice_payment') && r.payment_status !== 'paid' && r.payment_status !== 'cancel'"
                      :tooltip="$t('invoice.recordPaymentTooltip')"
                      class="oc-action-btn"
                      @click="openRecordPaymentModal(r)"
                    >
                      <i
                        class="fa fa-dollar"
                        style="color: #2e7d32"
                      ></i>
                    </a>

                    <a
                      :tooltip="$t('invoice.logsTooltip')"
                      class="oc-action-btn"
                      @click="openPaymentLogsModal(r.order_invoice_id)"
                    >
                      <i
                        class="fa fa-history"
                        style="color: #666"
                      ></i>
                    </a>

                    <a
                      v-if="$_has_perm('delete_shipping_invoice')"
                      :tooltip="$t('common.delete')"
                      class="oc-action-btn"
                      @click="deleteRow(r.order_invoice_id)"
                    >
                      <i
                        class="fa fa-trash error--text"
                        style="color: #e53935"
                      ></i>
                    </a>
                  </div>
                </td>
              </tr>
              <tr id="no-result-row">
                <td
                  :colspan="columns.length"
                  class="text-center"
                >
                  <v-alert
                    type="error"
                    :value="alertValue"
                  >
                    {{ $t('common.noResultsFound') }}
                  </v-alert>
                </td>
              </tr>
            </tbody>
          </datatable>
          <!-- Totals live outside the <table> on purpose: as a <tfoot>/<td colspan> they
               still fed the table's column-width algorithm and stretched the first column.
               A plain flex-wrap div below the table has zero effect on column widths. -->
          <div
            v-if="footer"
            class="oc-footer-totals"
          >
            <span style="color: #616161;">{{ $t('printLogs.totalAmountLabel') }}: {{ footer.total_amount }}</span>
            <span style="color: #2e7d32;">{{ $t('logs.totalPaidLabel') }} {{ footer.total_paid }}</span>
            <span style="color: #e57373;">{{ $t('logs.totalRemainingLabel') }} {{ footer.total_remaining }}</span>
            <span style="color: #c0392b;">{{ $t('customer.totalRefundLabel') }}: {{ footer.total_refund }}</span>
            <span style="color: #2e7d32;">{{ $t('logs.totalReceivedLabel') }} {{ footer.total_received }}</span>
            <span style="color: #616161;">{{ $t('reports.grandTotal') }}: {{ grandTotal }}</span>
            <span
              v-if="footer.cash_received !== null && footer.cash_received !== undefined"
              style="color: #1565c0;"
              :title="$t('invoice.cashReceivedInDaysHint')"
            >{{ $t('invoice.cashReceivedInDaysLabel') }} {{ footer.cash_received }}</span>
          </div>
          </div>

          <div class="row">
            <div class="col-xs-12 text-center oc-pagination">
              <paginate
                :page-count="pagination.lastPage"
                :page-range="2"
                :changePage="changePage"
                :container-class="'pagination'"
                :pagination="pagination"
                :page-class="'page-item'"
              >
              </paginate>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- View invoice: int. shipping merged into line Price/Total in viewOrderInvoice.vue (no separate Int. Shipping row). -->
    <view-order-invoice
      :viewOrderInvoiceModal="viewOrderInvoiceModal"
      :invoiceData="invoiceData"
      :companyData="companyData"
      :exRate="exRate"
      @close="viewOrderInvoiceModal = false"
    />

    <record-invoice-payment
      :recordPaymentModal="recordPaymentModal"
      :closeRecordPaymentModal="closeRecordPaymentModal"
      :recordPaymentData="recordPaymentData"
      :activeInvoice="activeInvoiceForPayment"
      :submitRecordPayment="submitRecordPayment"
      :loading="loadRecordPayment"
    />

    <invoice-payment-logs
      :logs="paymentLogsData"
      :closeModal="closeModal"
    />

    <bulk-print-order-invoices
      :printData="bulkPrintDataResults"
      :companyData="companyData"
      :exRate="exRate"
      :closeModal="closeModal"
    />

    <print-received-invoices
      :printData="receivedPrintDataResults"
      :companyData="companyData"
      :closeModal="closeModal"
    />

    <merge-print-order-invoices
      :printData="mergePrintDataResult"
      :companyData="companyData"
      :exRate="exRate"
      :closeModal="closeModal"
    />
  </div>
</template>

<script>
import PaginationVue from '../Pagination.vue'
import preDatatableComponent from '../preDatatableComponent.vue'
import DatatableVue from '../Datatable.vue'
import axios from 'axios'
import swal from 'sweetalert'
import ViewOrderInvoice from './viewOrderInvoice.vue'
import RecordInvoicePayment from './RecordInvoicePayment.vue'
import InvoicePaymentLogs from './InvoicePaymentLogs.vue'
import BulkPrintOrderInvoices from './BulkPrintOrderInvoices.vue'
import PrintReceivedInvoices from './PrintReceivedInvoices.vue'
import MergePrintOrderInvoices from './MergePrintOrderInvoices.vue'
import toolbarButtonDisplay from '../../mixins/toolbarButtonDisplay'

export default {
  mixins: [toolbarButtonDisplay],
  components: {
    preDatatableComponent: preDatatableComponent,
    datatable: DatatableVue,
    paginate: PaginationVue,
    ViewOrderInvoice,
    RecordInvoicePayment,
    InvoicePaymentLogs,
    BulkPrintOrderInvoices,
    PrintReceivedInvoices,
    MergePrintOrderInvoices
  },
  mounted() {
    this.perPageFix()
  },
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    // Suggested From/To values shown in the date pickers (2026-01-01 -> yesterday) - display
    // only, passed to preDatatableComponent as defaultFromDate/defaultToDate so the fields
    // aren't blank on load. Not applied to tableData.from_date/to_date, so no date filter is
    // actually sent until the user clicks the "Apply Filter" icon.
    const pad = (n) => String(n).padStart(2, '0')
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)
    const defaultFromDate = '2026-01-01'
    const defaultToDate = `${yesterday.getFullYear()}-${pad(yesterday.getMonth() + 1)}-${pad(yesterday.getDate())}`

    let sortOrders = {}
    // Merged per request: Invoice Info combines Invoice # + Warehouse (now the first data
    // column); Customer combines Customer ID + Name + Phone; Shipment Costs combines
    // Shipment/Tax/Delivery Price; Pricing combines Total/Discount/Amount; Payment combines
    // Paid/Remaining/Refund. Positions shifted, so storageKey is bumped (v5 -> v6) so stale
    // v5 hidden-column selections referencing old positions aren't silently misapplied.
    let columns = [
      {
        label: '',
        text: this.$t('columns.checkBoxColumn'),
        value: '1',
        sortable: false,
        searchable: false,
        name: ''
      },
      {
        label: this.$t('columns.invoiceInfo'),
        text: this.$t('columns.invoiceInfo'),
        value: '2',
        sortable: true,
        searchable: true,
        name: 'invoice_number'
      },
      {
        label: this.$t('columns.orderHash'),
        text: this.$t('columns.orderHash'),
        value: '3',
        sortable: false,
        searchable: true,
        name: 'order_ids'
      },
      {
        label: this.$t('columns.customer'),
        text: this.$t('columns.customer'),
        value: '4',
        sortable: true,
        searchable: true,
        name: 'full_name'
      },
      {
        label: this.$t('columns.customerNote'),
        text: this.$t('columns.customerNote'),
        value: '5',
        sortable: true,
        searchable: true,
        name: 'customer_note'
      },
      {
        label: this.$t('columns.shipOnly'),
        text: this.$t('columns.shipOnly'),
        value: '6',
        sortable: true,
        searchable: true,
        name: 'shiponly'
      },
      {
        label: this.$t('columns.weight'),
        text: this.$t('columns.weight'),
        value: '7',
        sortable: true,
        searchable: true,
        name: 'weight'
      },
      {
        label: this.$t('columns.volume'),
        text: this.$t('columns.volume'),
        value: '8',
        sortable: true,
        searchable: true,
        name: 'volume'
      },
      {
        label: this.$t('columns.handlingFee'),
        text: this.$t('columns.handlingFee'),
        value: '9',
        sortable: true,
        searchable: true,
        name: 'handling_fee'
      },
      {
        label: this.$t('columns.shipmentCosts'),
        text: this.$t('columns.shipmentCosts'),
        value: '10',
        sortable: true,
        searchable: false,
        name: 'shipment'
      },
      {
        label: this.$t('columns.pricing'),
        text: this.$t('columns.pricing'),
        value: '11',
        sortable: true,
        searchable: false,
        name: 'net_price'
      },
      {
        label: this.$t('columns.payment'),
        text: this.$t('columns.payment'),
        value: '12',
        sortable: true,
        searchable: false,
        name: 'paid_amount'
      },
      {
        label: this.$t('columns.status'),
        text: this.$t('columns.status'),
        value: '13',
        sortable: false,
        searchable: false,
        name: 'payment_status'
      },
      {
        label: this.$t('columns.outOffice'),
        text: this.$t('columns.outOffice'),
        value: '14',
        sortable: false,
        searchable: false,
        name: 'office_status'
      },
      {
        label: this.$t('columns.owner'),
        text: this.$t('columns.owner'),
        value: '15',
        sortable: false,
        searchable: false,
        name: 'owner'
      },
      {
        label: this.$t('columns.receivedDate'),
        text: this.$t('columns.receivedDate'),
        value: '16',
        sortable: true,
        searchable: false,
        name: 'received_at'
      },
      {
        label: this.$t('columns.receivedAmount'),
        text: this.$t('columns.receivedAmount'),
        value: '17',
        sortable: true,
        searchable: false,
        name: 'received_amount'
      },
      {
        label: this.$t('columns.note'),
        text: this.$t('columns.note'),
        value: '18',
        sortable: true,
        searchable: true,
        name: 'note'
      },
      {
        label: this.$t('columns.createdAt'),
        text: this.$t('columns.createdAt'),
        value: '19',
        sortable: true,
        searchable: true,
        name: 'created_at'
      },
      {
        label: this.$t('columns.deliveryAddress'),
        text: this.$t('columns.deliveryAddress'),
        value: '20',
        sortable: false,
        searchable: false,
        name: 'delivery_address'
      },
      {
        label: this.$t('columns.generatedBy'),
        text: this.$t('columns.generatedBy'),
        value: '21',
        sortable: false,
        searchable: false,
        name: 'generated_by'
      },
      {
        label: this.$t('columns.actions'),
        text: this.$t('columns.actions'),
        value: '22',
        sortable: false,
        searchable: false,
        name: 'actions'
      }
    ]
    columns.forEach((column) => {
      sortOrders[column.name] = -1
    })

    return {
      defaultFromDate: defaultFromDate,
      defaultToDate: defaultToDate,
      url: 'api/order_invoice',
      alertValue: false,
      btnsave: true,
      viewOrderInvoiceModal: false,
      invoiceData: {
        customer: {},
        orders: [],
        second_orders: []
      },
      exRate: 1,
      companyData: {},
      filterMade: false,
      totalResult: 0,
      prevSearchQuery: '',
      apiResults: [],
      columns: columns,
      sortKey: 'order_invoice_id',
      sortOrders: sortOrders,
      // Hides Customer Note(5)/Weight(7)/Volume(8)/Handling Fee(9)/Note(18) by default -
      // kept in sync with the columns array above (positions renumbered by the
      // Invoice Info/Customer/Shipment Costs/Pricing/Payment merges; storageKey bumped
      // to v6 so stale v5 hidden-column selections referencing old positions aren't
      // silently applied to the wrong column).
      defaultHideCol: ['5', '7', '8', '9', '18'],
      // Hidden by default (per-page preference, same localStorage convention as allOrder.vue).
      showFilters: localStorage.getItem('filtersVisible_orderInvoices') === 'true',
      footer: null,
      resultsVersion: 0,
      selectedIds: [],
      recordPaymentModal: false,
      recordPaymentData: {
        amount: null,
        note: '',
        payment_method: ''
      },
      activeInvoiceForPayment: {},
      loadRecordPayment: false,
      paymentLogsData: [],
      bulkPrintDataResults: [],
      receivedPrintDataResults: [],
      mergePrintDataResult: { customer: {}, orders: [], second_orders: [] },
      tableData: {
        per_page: 10,
        search: '',
        column: 'order_invoices.updated_at',
        sortDir: 'desc',
        searchOneCol: '',
        searchOneVal: '',
        from_date: '',
        to_date: '',
        date_field: 'created_at',
        branches_id: '',
        status_filter: '',
        received_filter: '',
        generated_by_filter: '',
        office_status_filter: '',
        refund_filter: ''
      },
      paymentStatusFilterItems: [
        { text: this.$t('invoice.paidStatus'), value: 'paid' },
        { text: this.$t('invoice.partialPaidStatus'), value: 'partial_paid' },
        { text: this.$t('invoice.unpaidStatus'), value: 'not_paid' },
        { text: this.$t('orders.cancelTooltip'), value: 'cancel' }
      ],
      dateFieldFilterItems: [
        { text: this.$t('columns.createdAt'), value: 'created_at' },
        { text: this.$t('invoice.receivedAtOption'), value: 'received_at' }
      ],
      generatedByFilterItems: [
        { text: this.$t('invoice.singleOption'), value: 'approval_single' },
        { text: this.$t('invoice.batchOption'), value: 'approval_batch' },
        { text: this.$t('invoice.digitalOption'), value: 'auto_digital' },
        { text: this.$t('invoice.officeOption'), value: 'auto_office' },
        { text: this.$t('invoice.manualOption'), value: 'manual' }
      ],
      generatedByFilterVal: [],
      officeStatusFilterItems: [
        { text: this.$t('invoice.officeOption'), value: 'office' },
        { text: this.$t('columns.outOffice'), value: 'out_office' }
      ],
      officeStatusFilterVal: [],
      refundFilterItems: [
        { text: this.$t('invoice.refundExistsOption'), value: 'exists' },
        { text: this.$t('invoice.noRefundOption'), value: 'null' }
      ],
      refundFilterVal: [],
      pagination: {
        lastPage: 1,
        currentPage: 1,
        total: 1,
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
        from: '',
        to: ''
      }
    }
  },

  computed: {
    grandTotal() {
      if (!this.footer) return null
      const num = (v) => parseFloat(v) || 0
      return (
        num(this.footer.total_paid) -
        num(this.footer.total_received)
      ).toFixed(2)
    }
  },

  methods: {
    sendToExport() {
      let searchValue = this.tableData.search !== '' ? this.tableData.search : '0'
      let column = this.tableData.column !== '' ? this.tableData.column : '0'
      let sortDir = this.tableData.sortDir !== '' ? this.tableData.sortDir : '0'
      let searchedOneCol = this.tableData.searchOneCol !== '' ? this.tableData.searchOneCol : '0'
      let searchedOneVal = this.tableData.searchOneVal !== '' ? this.tableData.searchOneVal : '0'
      let fromDate = this.tableData.from_date !== '' ? this.tableData.from_date : '0'
      let toDate = this.tableData.to_date !== '' ? this.tableData.to_date : '0'
      let dateField = this.tableData.date_field !== '' ? this.tableData.date_field : 'created_at'
      let statusFilter = this.tableData.status_filter !== '' ? this.tableData.status_filter : '0'
      let receivedFilter = this.tableData.received_filter !== '' ? this.tableData.received_filter : '0'
      let generatedByFilter = this.tableData.generated_by_filter !== '' ? this.tableData.generated_by_filter : '0'
      let officeStatusFilter = this.tableData.office_status_filter !== '' ? this.tableData.office_status_filter : '0'

      let hostName = window.location.hostname
      let protocol = window.location.protocol + '//'
      let fullHostName = protocol + hostName

      window.location.replace(
        fullHostName +
        '/api/order_invoice/export?searchValue=' +
        searchValue +
        '&column=' +
        column +
        '&sortDir=' +
        sortDir +
        '&searchedOneCol=' +
        searchedOneCol +
        '&searchedOneVal=' +
        searchedOneVal +
        '&from_date=' +
        fromDate +
        '&to_date=' +
        toDate +
        '&date_field=' +
        dateField +
        '&status_filter=' +
        statusFilter +
        '&received_filter=' +
        receivedFilter +
        '&generated_by_filter=' +
        generatedByFilter +
        '&office_status_filter=' +
        officeStatusFilter
      )
    },

    branchEdit(branches) {
      this.tableData.branches_id = branches.toString()
      this.getResults()
    },

    toggleFiltersVisible() {
      this.showFilters = !this.showFilters
      localStorage.setItem('filtersVisible_orderInvoices', this.showFilters ? 'true' : 'false')
    },

    // Replicates preDatatableComponent.vue's own built-in Reload button (now relocated
    // into the panel-heading here) -- reaches into the child's own filter-chip/date-range
    // reset state via $refs since that state lives inside the shared component, not here.
    reloadTable() {
      this.resetGetResults()
      const preDatatable = this.$refs.preDatatable
      if (preDatatable) {
        preDatatable.filterApplied = false
        preDatatable.resetDatePickerDefaults()
        preDatatable.ware = 'Select'
      }
    },

    // Deterministic by design: explicitly hide()/show() every column position
    // based only on currentColVal (the set of columns that should be hidden),
    // instead of diffing against prevColVal and .toggle()-ing the delta.
    // jQuery's .toggle() flips each matched <td>/<th> based on its OWN
    // current display state — since Vue reuses some <tr> rows (keeping an
    // earlier inline style) and recreates others (starting fresh/visible)
    // on every table reload, a blanket toggle desyncs rows from each other
    // instead of applying one consistent state. Explicit set/unset is
    // idempotent regardless of how mixed the current DOM state is or how
    // many times it's called.
    showHideColumn(prevColVal, currentColVal) {
      const hidden = {}
      currentColVal.forEach((v) => { hidden[v] = true })

      for (let i = 1; i <= this.columns.length; i++) {
        const selector = '#datatable td:nth-child(' + i + '),#datatable th:nth-child(' + i + ')'
        if (hidden[i]) {
          $(selector).hide()
        } else {
          $(selector).show()
        }
      }
    },

    getCompanyData(token) {
      axios
        .get('api/company_info', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          this.companyData = response.data.data
        })
    },

    viewRecord(id) {
      let token = localStorage.getItem('access_token')
      this.getCompanyData(token)

      axios.get('api/currency-rate/latest', { headers: { Authorization: 'Bearer ' + token } })
        .then((res) => { this.exRate = res.data ? res.data.ex_rate || 1 : 1 })
        .catch(() => {})

      axios
        .get(`api/order_invoice/${id}`, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then((res) => {
          this.viewOrderInvoiceModal = true
          this.invoiceData = res.data
        })
    },

    deleteRow(id) {
      swal({
        title: this.$t('common.areYouSure'),
        icon: 'warning',
        buttons: true
      }).then(willDelete => {
        if (willDelete) {
          let token = localStorage.getItem('access_token')
          axios
            .delete(`api/order_invoice/${id}`, { headers: { Authorization: 'Bearer ' + token } })
            .then(() => {
              this.getResults()
              swal({
                title: this.$t('common.done'),
                text: '',
                icon: 'success',
                timer: 1500,
                buttons: false
              })
            })
            .catch((error) => {
              const msg = (error.response && error.response.data && error.response.data.message) || this.$t('invoice.failedToDeleteInvoice')
              swal(this.$t('common.error'), typeof msg === 'string' ? msg : this.$t('invoice.failedToDeleteInvoice'), 'error')
            })
        }
      })
    },

    getResults(url = this.url) {
      var token = localStorage.getItem('access_token')
      let theDatatable = document.querySelector('.the-datatable')
      let thePanel = document.querySelector('.panel-flat')

      // A selection made before a filter/search/reload must not silently carry over into
      // the new result set — otherwise a stale row (no longer even visible) stays selected
      // and gets included in the next bulk action (Fast Payment, Mark Received, etc.),
      // affecting rows the user never intended to touch in the current (filtered) view.
      this.selectedIds = []

      if (theDatatable != null) {
        theDatatable.classList.add('slide-table-out')
        thePanel.style.overflowX = 'auto'
      }

      axios
        .get(url, { params: this.tableData, headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let data = response.data
          if (data.data.footer) {
            this.footer = data.data.footer
          }
          const isFlatArray = Array.isArray(data.data)
          if (isFlatArray) {
            this.filterMade = true
            this.apiResults = data.data
          } else {
            this.filterMade = false
            this.apiResults = data.data.data
          }
          this.resultsVersion++

          if (this.apiResults.length === 0) {
            this.tableData.searchOneCol = ''
            this.tableData.searchOneVal = ''
            this.tableData.search = ''
            this.alertValue = true
          } else {
            this.alertValue = false
          }
          if (isFlatArray) {
            this.removeConfigPagination()
          } else {
            this.configPagination(data.data)
          }
          theDatatable.classList.remove('slide-table-out')
          setTimeout(function () {
            thePanel.style.overflowX = 'auto'
          }, 0)
        })
        .then(() => {
          this.totalResult = 0
          for (let i = 0; i < this.apiResults.length; i++) {
            this.totalResult += 1
          }
        })
        .catch((errors) => { })
    },

    removeConfigPagination() {
      this.pagination.lastPage = ''
      this.pagination.currentPage = ''
      this.pagination.total = ''
      this.pagination.lastPageUrl = ''
      this.pagination.nextPageUrl = ''
      this.pagination.prevPageUrl = ''
      this.pagination.from = ''
      this.pagination.to = ''
    },

    resetGetResults() {
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      this.tableData.search = ''
      this.sortKey = 'updated_at'
      this.tableData.sortDir = 'desc'
      this.tableData.column = 'updated_at'
      this.tableData.from_date = ''
      this.tableData.to_date = ''
      this.tableData.date_field = 'created_at'
      this.tableData.status_filter = ''
      this.tableData.received_filter = ''
      this.tableData.generated_by_filter = ''
      this.generatedByFilterVal = []
      this.tableData.office_status_filter = ''
      this.officeStatusFilterVal = []
      this.tableData.refund_filter = ''
      this.refundFilterVal = []
      this.getResults()
    },

    resetGetResults2() {
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      this.tableData.search = ''
      this.sortKey = 'updated_at'
      this.tableData.sortDir = 'desc'
      this.tableData.column = 'updated_at'
      this.tableData.from_date = ''
      this.tableData.to_date = ''
      this.tableData.date_field = 'created_at'
      this.tableData.status_filter = ''
      this.tableData.received_filter = ''
      this.tableData.generated_by_filter = ''
      this.generatedByFilterVal = []
      this.tableData.office_status_filter = ''
      this.officeStatusFilterVal = []
      this.tableData.refund_filter = ''
      this.refundFilterVal = []
      this.getResults()
    },

    handlePaymentStatusFilter(val) {
      this.tableData.status_filter = val && val.length > 0 ? val.join(',') : ''
      this.getResults()
    },

    handleReceivedFilter(val) {
      this.tableData.received_filter = val && val.length > 0 ? val.join(',') : ''
      this.getResults()
    },

    handleGeneratedByFilter(val) {
      this.generatedByFilterVal = val || []
      this.tableData.generated_by_filter = val && val.length > 0 ? val.join(',') : ''
      this.getResults()
    },

    handleOfficeStatusFilter(val) {
      this.officeStatusFilterVal = val || []
      this.tableData.office_status_filter = val && val.length > 0 ? val.join(',') : ''
      this.getResults()
    },

    handleRefundFilter(val) {
      this.refundFilterVal = val || []
      this.tableData.refund_filter = val && val.length > 0 ? val.join(',') : ''
      this.getResults()
    },

    perPageFix(perPage) {
      this.tableData.per_page = perPage
      this.getResults()
    },

    changePage(pageUrl) {
      this.url = pageUrl
      this.getResults()
    },

    searchMe(str) {
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      if (str.trim() !== '') {
        this.tableData.search = str
        this.prevSearchQuery = str
        this.getResults()
      } else {
        swal({
          text: this.$t('common.typeToSearch'),
          icon: 'warning',
          dangerMode: true
        })
        if (this.prevSearchQuery !== '') {
          this.tableData.search = ''
          this.prevSearchQuery = ''
          this.getResults()
        }
      }
    },

    sortByColumn(str) {
      this.tableData.column = str
      this.tableData.sortKey = str
      this.tableData.sortDir = this.tableData.sortDir === 'desc' ? 'asc' : 'desc'
      this.getResults()
    },

    searchOneCol(columnName, str) {
      this.tableData.searchOneCol = columnName
      this.tableData.searchOneVal = str
      this.getResults()
    },

    filterTable(fromDate, toDate) {
      this.tableData.from_date = fromDate
      this.tableData.to_date = toDate
      this.getResults()
    },

    configPagination(data) {
      this.pagination.lastPage = data.last_page
      this.pagination.currentPage = data.current_page
      this.pagination.total = data.total
      this.pagination.lastPageUrl = data.last_page_url
      this.pagination.nextPageUrl = data.next_page_url
      this.pagination.prevPageUrl = data.prev_page_url
      this.pagination.from = data.from
      this.pagination.to = data.to
    },

    checkAll() {
      this.selectedIds = this.apiResults.map(r => r.order_invoice_id)
    },

    unCheckAll() {
      this.selectedIds = []
    },

    statusColor(status, editCount) {
      // Auto-generated invoices (created automatically at order approval, never yet touched by
      // staff via Generate Invoice — see edit_count) show grey instead of the urgent red used for
      // a genuinely unpaid invoice, since there's nothing to chase yet: no shipment cost has been
      // added and no one has started working on it.
      if (status === 'not_paid' && !Number(editCount)) {
        return { bg: '#E0E0E0', color: '#616161' }
      }

      const map = {
        not_paid: { bg: '#F63049', color: '#FFFFFF' },
        partial_paid: { bg: '#FFC85C', color: '#6B4C00' },
        paid: { bg: '#89D7B7', color: '#1A5940' },
        cancel: { bg: '#FB8C00', color: '#FFFFFF' }
      }
      return map[status] || { bg: '#F5F5F5', color: '#616161' }
    },

    statusLabel(status) {
      if (!status) return ''
      const map = {
        not_paid: this.$t('invoice.unpaidStatus'),
        partial_paid: this.$t('invoice.partialPaidStatus'),
        paid: this.$t('invoice.paidStatus'),
        cancel: this.$t('invoice.cancelStatus')
      }
      return map[status] || status.replace(/_/g, ' ')
    },

    // Mirrors OrderInvoice::GENERATED_* constants (app/OrderInvoice.php) — how the invoice
    // first came into existence, set once at creation and never changed afterward.
    generatedByLabel(generatedBy) {
      const map = {
        approval_single: this.$t('invoice.singleOption'),
        approval_batch: this.$t('invoice.batchOption'),
        auto_digital: this.$t('invoice.digitalOption'),
        auto_office: this.$t('invoice.officeOption'),
        manual: this.$t('invoice.manualOption')
      }
      return map[generatedBy] || null
    },

    generatedByColor(generatedBy) {
      const map = {
        approval_single: { bg: '#E3F2FD', color: '#01579B' },
        approval_batch: { bg: '#D1C4E9', color: '#4527A0' },
        auto_digital: { bg: '#C8E6C9', color: '#1B5E20' },
        auto_office: { bg: '#FFE0B2', color: '#E65100' },
        manual: { bg: '#F5F5F5', color: '#424242' }
      }
      return map[generatedBy] || { bg: '#F5F5F5', color: '#616161' }
    },

    formatReceivedDate(date) {
      if (!date) return ''
      return this.$moment(date).format('YYYY-MM-DD hh:mm a')
    },

    isFullyReceived(r) {
      // received_amount is null for invoices marked received before this column
      // existed (legacy rows, never backfilled) — treat those as fully received,
      // matching the old all-or-nothing meaning of "received" they were saved under.
      if (r.received_amount === null || r.received_amount === undefined) return true
      return Number(r.received_amount) >= Number(r.net_price)
    },

    receivedStatusColor(r) {
      return this.isFullyReceived(r)
        ? { bg: '#89D7B7', color: '#1A5940' }
        : { bg: '#FFC85C', color: '#6B4C00' }
    },

    receivedStatusLabel(r) {
      return this.isFullyReceived(r) ? this.$t('orders.receivedStatusTitle') : this.$t('delivery.partialReceivedStatus')
    },

    officeStatusColor(r) {
      return r.office_status === 'out_office'
        ? { bg: '#F63049', color: '#FFFFFF' }
        : { bg: '#89D7B7', color: '#1A5940' }
    },

    officeStatusLabel(r) {
      return r.office_status === 'out_office' ? this.$t('columns.outOffice') : this.$t('invoice.officeOption')
    },

    modal(modal) {
      $('#' + modal).modal({ backdrop: 'static' })
    },

    closeModal(modal) {
      let theModal = $('#' + modal)
      theModal.modal('toggle')
    },

    openRecordPaymentModal(r) {
      this.activeInvoiceForPayment = r
      const remaining = parseFloat(r.remaining_amount)
      this.recordPaymentData = {
        amount: remaining > 0 ? remaining : null,
        note: '',
        payment_method: '',
        already_in_credit: false
      }
      this.recordPaymentModal = true
    },

    closeRecordPaymentModal() {
      this.recordPaymentModal = false
    },

    submitRecordPayment() {
      this.loadRecordPayment = true
      let token = localStorage.getItem('access_token')
      axios
        .put(`api/order_invoice/${this.activeInvoiceForPayment.order_invoice_id}`, this.recordPaymentData, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then((response) => {
          this.loadRecordPayment = false
          if (response.data.success === false) {
            swal({ text: response.data.message, icon: 'error' })
            return
          }
          this.recordPaymentModal = false
          this.getResults()
          swal({
            title: this.$t('common.done'),
            text: '',
            icon: 'success',
            timer: 1500,
            buttons: false
          })
        })
        .catch((err) => {
          this.loadRecordPayment = false
          const msg = (err.response && err.response.data && err.response.data.message)
            ? err.response.data.message
            : this.$t('invoice.somethingWentWrong')
          swal({ text: msg, icon: 'error' })
        })
    },

    openPaymentLogsModal(id) {
      let token = localStorage.getItem('access_token')
      axios
        .get(`api/order_invoice/${id}/payment-logs`, { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          this.paymentLogsData = response.data.data
          this.modal('order_invoice_logs_modal')
        })
    },

    confirmFastPaymentAll() {
      if (this.selectedIds.length === 0) {
        swal({ text: this.$t('invoice.pleaseSelectAtLeastOneInvoice'), icon: 'warning' })
        return
      }
      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('invoice.confirmMarkAllPaid'),
        icon: 'warning',
        buttons: true
      }).then(willPay => {
        if (willPay) {
          this.submitFastPayment()
        }
      })
    },

    submitFastPayment() {
      let token = localStorage.getItem('access_token')
      axios
        .post('api/order_invoice/fast-payment', { order_invoice_ids: this.selectedIds }, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then((response) => {
          this.unCheckAll()
          this.getResults()
          swal({ text: response.data.message, icon: 'success' })
        })
        .catch((err) => {
          const msg = (err.response && err.response.data && err.response.data.message)
            ? err.response.data.message
            : this.$t('invoice.somethingWentWrong')
          swal({ text: msg, icon: 'error' })
        })
    },

    prepareToPrint() {
      if (this.selectedIds.length === 0) {
        swal({ text: this.$t('invoice.pleaseSelectAtLeastOneInvoice'), icon: 'warning' })
        return
      }
      let token = localStorage.getItem('access_token')
      this.getCompanyData(token)
      axios
        .get('api/order_invoice/bulk-print', {
          params: { ids: this.selectedIds },
          headers: { Authorization: 'Bearer ' + token }
        })
        .then((response) => {
          this.bulkPrintDataResults = response.data.data
          this.modal('bulk_print_order_invoices_modal')
        })
    },

    confirmReceiveAll() {
      if (this.selectedIds.length === 0) {
        swal({ text: this.$t('invoice.pleaseSelectAtLeastOneInvoice'), icon: 'warning' })
        return
      }
      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('invoice.confirmMarkAllReceived'),
        icon: 'warning',
        buttons: true
      }).then(willReceive => {
        if (willReceive) {
          let token = localStorage.getItem('access_token')
          axios
            .post('api/order_invoice/mark-received', { order_invoice_ids: this.selectedIds }, {
              headers: { Authorization: 'Bearer ' + token }
            })
            .then((response) => {
              this.unCheckAll()
              this.getResults()
              swal({ text: response.data.message, icon: 'success' })
            })
            .catch(() => {
              swal({ text: this.$t('invoice.somethingWentWrong'), icon: 'error' })
            })
        }
      })
    },

    confirmUnreceiveAll() {
      if (this.selectedIds.length === 0) {
        swal({ text: this.$t('invoice.pleaseSelectAtLeastOneInvoice'), icon: 'warning' })
        return
      }
      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('invoice.confirmUnmarkAllReceived'),
        icon: 'warning',
        buttons: true
      }).then(willUnreceive => {
        if (willUnreceive) {
          let token = localStorage.getItem('access_token')
          axios
            .post('api/order_invoice/unmark-received', { order_invoice_ids: this.selectedIds }, {
              headers: { Authorization: 'Bearer ' + token }
            })
            .then((response) => {
              this.unCheckAll()
              this.getResults()
              swal({ text: response.data.message, icon: 'success' })
            })
            .catch(() => {
              swal({ text: this.$t('invoice.somethingWentWrong'), icon: 'error' })
            })
        }
      })
    },

    confirmOutOfficeAll() {
      if (this.selectedIds.length === 0) {
        swal({ text: this.$t('invoice.pleaseSelectAtLeastOneInvoice'), icon: 'warning' })
        return
      }
      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('invoice.confirmToggleOutOffice'),
        icon: 'warning',
        buttons: true
      }).then(willMark => {
        if (willMark) {
          let token = localStorage.getItem('access_token')
          axios
            .post('api/order_invoice/mark-out-office', { order_invoice_ids: this.selectedIds }, {
              headers: { Authorization: 'Bearer ' + token }
            })
            .then((response) => {
              this.unCheckAll()
              this.getResults()
              swal({ text: response.data.message, icon: 'success' })
            })
            .catch(() => {
              swal({ text: this.$t('invoice.somethingWentWrong'), icon: 'error' })
            })
        }
      })
    },

    receivePrintList() {
      if (this.selectedIds.length === 0) {
        swal({ text: this.$t('invoice.pleaseSelectAtLeastOneInvoice'), icon: 'warning' })
        return
      }
      let token = localStorage.getItem('access_token')
      this.getCompanyData(token)
      axios
        .get('api/order_invoice/receive-print-data', {
          params: { ids: this.selectedIds },
          headers: { Authorization: 'Bearer ' + token }
        })
        .then((response) => {
          this.receivedPrintDataResults = response.data.data
          this.modal('print_received_invoices_modal')
        })
    },

    mergePrintList() {
      if (this.selectedIds.length < 2) {
        swal({ text: this.$t('invoice.pleaseSelectAtLeastTwoPaidInvoices'), icon: 'warning' })
        return
      }
      let token = localStorage.getItem('access_token')
      this.getCompanyData(token)
      axios.get('api/currency-rate/latest', { headers: { Authorization: 'Bearer ' + token } })
        .then((res) => { this.exRate = res.data ? res.data.ex_rate || 1 : 1 })
        .catch(() => {})

      axios
        .get('api/order_invoice/merge-print', {
          params: { ids: this.selectedIds },
          headers: { Authorization: 'Bearer ' + token }
        })
        .then((response) => {
          this.mergePrintDataResult = response.data.data
          this.modal('merge_print_order_invoices_modal')
        })
        .catch((error) => {
          const msg = (error.response && error.response.data && error.response.data.message) || this.$t('logs.failedToMergeSelectedInvoices')
          swal({ text: msg, icon: 'error' })
        })
    }
  }
}
</script>

<style>
.table > thead > tr > th, .table > tbody > tr > th, .table > tfoot > tr > th, .table > thead > tr > td, .table > tbody > tr > td, .table > tfoot > tr > td
{
  padding: 0 5px !important;
}

/* Footer totals strip -- rendered as a div AFTER </datatable>, not a <tfoot>, so it has no
   say in the table's column widths (a <td colspan> still does, and was stretching the first
   column). flex-wrap lets the six totals sit next to each other and wrap to a new line only
   when the container is narrow, so it never adds horizontal scroll. */
.oc-footer-totals {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px 20px;
  padding: 10px 12px;
  font-weight: bold;
  border-top: 1px solid #eee;
}
.oc-footer-totals > span {
  white-space: nowrap;
}

@media screen and (max-width: 600px) {
  th {
    font-weight: bold;
    color: #273246;
    padding-left: 5px !important;
  }

  td {
    padding-left: 5px !important;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
}

.panel-heading h4 {
  text-transform: uppercase;
  margin-left: -20px;
  padding-left: 10px;
  font-weight: 500;
}

.col-xs-12 {
  padding-top: 10px;
  padding-bottom: 10px;
}
.overlay--active:before {
  opacity: 50 !important;
  background: rgba(0, 0, 0, 0.5) !important;
}

.dialog {
  box-shadow: 0 3px 5px -1px rgba(14, 14, 14, 0.212) !important;
  overflow: hidden;
  overflow-y: hidden;
}
.page-stats {
  align-items: center;
  position: relative;
  top: -8px;
  margin-right: 5px;
}

.has-error .select2-selection--single:not([class*='bg-']):not([class*='border-']) {
  border-bottom-color: red;
}

a:hover {
  color: #03a9f4;
  position: relative;
}
a[tooltip]:hover:after {
  content: attr(tooltip);
  padding: 8px 12px;
  color: #ffffff;
  position: absolute;
  right: 80%;
  bottom: 150%;
  white-space: nowrap;
  z-index: 20;
  -moz-border-radius: 3px;
  -webkit-border-radius: 3px;
  border-radius: 3px;
  -moz-box-shadow: 0px 0px 2px #484b4e;
  -webkit-box-shadow: 0px 0px 2px #484b4e;
  box-shadow: 0px 0px 2px #484b4e;
  background-image: -moz-linear-gradient(top, #484b4e, #484b4e);
  background-image: -webkit-gradient(
    linear,
    left top,
    left bottom,
    color-stop(0, #484b4e),
    color-stop(1, #484b4e)
  );
  background-image: -webkit-linear-gradient(top, #484b4e, #484b4e);
  background-image: -moz-linear-gradient(top, #484b4e, #484b4e);
  background-image: -ms-linear-gradient(top, #484b4e, #484b4e);
  background-image: -o-linear-gradient(top, #484b4e, #484b4e);
}

@media screen and (max-width: 767px) {
  .form-group {
    margin-top: 0;
    margin-bottom: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/orderInvoice/orderInvoices.vue